import { useState } from 'react';
import { Link, useNavigate, useRouteLoaderData } from 'react-router';
import QueueModal from '../components/QueueModal';
import BadgeList from '../components/onboarding/BadgeList';
import CrewCard from '../components/onboarding/CrewCard';
import ProgressRail from '../components/onboarding/ProgressRail';
import StepBar from '../components/StepBar';
import { ROLES, type UserType } from '../lib/onboarding/roles';
import {
  completion,
  crewNumber,
  disciplineOf,
  earnedXp,
  maxXp,
  nextAction,
  rankOf,
  stepComplete
} from '../lib/onboarding/progress';
import { stepsFor } from '../lib/onboarding/steps';
import { signOutSupabase, SITE_URL } from '../lib/supabase';
import { TRAILER_ID } from '../lib/trailer';
import type { RootLoaderData } from '../lib/types';

interface Cta {
  to: string;
  title: string;
  body: string;
}

/** What each role should be doing next, once the profile is out of the way. */
const CTAS: Record<UserType, Cta[]> = {
  professional: [
    { to: '/find-projects', title: 'Find projects', body: 'Briefs from companies, filtered to your craft, tools and availability.' },
    { to: '/portfolio', title: 'Build the portfolio', body: 'One reel and three projects is the difference between listed and hired.' }
  ],
  company: [
    { to: '/requirements', title: 'Post a requirement', body: 'Five steps from an idea to a published brief in front of matching crews.' },
    { to: '/find-talent', title: 'Find talent', body: 'Search by craft, tool and availability. Shortlist, message, hire.' }
  ],
  student: [
    { to: '/academy', title: 'Start the syllabus', body: 'Your tracks, in order, with assignments that end in finished work.' },
    { to: '/projects', title: 'Build the portfolio', body: 'Coursework here becomes portfolio work — and portfolio work becomes offers.' }
  ],
  beginner: [
    { to: '/academy', title: 'Continue learning', body: 'Short tracks sized to the time you said you had.' },
    { to: '/projects', title: 'Make the first one', body: 'Small and finished beats big and abandoned. Start one today.' }
  ]
};

export default function Dashboard() {
  const { identity, profile } = useRouteLoaderData('root') as RootLoaderData;
  const navigate = useNavigate();
  const [queueOpen, setQueueOpen] = useState(false);

  // rootLoader guarantees a finished induction, so the role is always set.
  const userType = profile.userType as UserType;
  const role = ROLES[userType];
  const answers = profile.answers;

  const steps = stepsFor(userType);
  const percent = completion(userType, answers);
  const rank = rankOf(userType, percent);
  const next = nextAction(userType, answers);
  const earned = new Set(steps.filter(step => stepComplete(step, answers)).map(step => step.badge.id));

  const leave = async () => {
    await signOutSupabase();
    navigate('/signed-out', { replace: true });
  };

  return (
    <>
      <StepBar left={`${role.label} · ${rank.label}`} />

      <CrewCard
        name={(answers.full_name as string) || identity.name}
        email={identity.email}
        avatarUrl={identity.avatarUrl}
        crewNo={crewNumber(profile.memberNo)}
        roleLabel={role.label}
        rank={rank.label}
        discipline={disciplineOf(userType, answers)}
        percent={percent}
      />

      <div className="rule" />

      <ProgressRail
        rank={rank.label}
        nextRank={rank.next}
        toNext={rank.toNext}
        xp={earnedXp(userType, answers)}
        maxXp={maxXp(userType)}
        percent={percent}
        steps={steps.length}
        stepsDone={steps.filter(step => stepComplete(step, answers)).length}
      />

      {/* One concrete thing, priced — never a list of everything still missing. */}
      {next ? (
        <div className="panel">
          <h2>{next.field.label}</h2>
          <p>
            {next.field.hint || next.step.blurb} Adding it is worth <b style={{ color: 'var(--mag)' }}>{next.field.xp} XP</b>
            {rank.next ? ` and moves you toward ${rank.next}.` : '.'}
          </p>
          <div className="actions" style={{ marginTop: 18 }}>
            <Link className="btn" to="/profile">
              Add it →
            </Link>
          </div>
        </div>
      ) : (
        <div className="panel">
          <h2>Profile complete.</h2>
          <p>Every question answered. You are as discoverable as this platform can make you.</p>
        </div>
      )}

      <div className="rule" />

      <div className="grid2">
        {CTAS[userType].map(cta => (
          <Link key={cta.to} to={cta.to} className="panel">
            <h2>{cta.title}</h2>
            <p>{cta.body}</p>
          </Link>
        ))}
      </div>

      <div className="rule" />
      <BadgeList badges={steps.map(step => ({ id: step.badge.id, label: step.badge.label, earned: earned.has(step.badge.id) }))} />
      <div className="rule" />

      <p className="ecosystem">
        <b>Learn</b> <i>→</i> <b>Create</b> <i>→</i> <b>Hire</b>
      </p>

      <div className="actions" style={{ marginTop: 26 }}>
        <button className="btn" type="button" onClick={() => setQueueOpen(true)}>
          Explore →
        </button>
        <button className="btn ghost" type="button" onClick={leave}>
          Sign out
        </button>
      </div>

      {queueOpen ? (
        <QueueModal videoId={TRAILER_ID} exploreUrl={SITE_URL} onClose={() => setQueueOpen(false)} />
      ) : null}
    </>
  );
}
